import React, { useState, useEffect, useCallback } from "react";
import { Users, Eye, Globe2, FileText, Loader2, ShieldCheck, Compass } from "lucide-react";
import { apiClient } from "../../lib/api";

const RANGES = [
    [7, "7 Tage"],
    [14, "14 Tage"],
    [30, "30 Tage"],
];

const pathLabel = (p) => {
    if (p === "/") return "Startseite";
    if (p.startsWith("/stelle/")) return `Stelle: ${p.replace("/stelle/", "")}`;
    if (p === "/impressum") return "Impressum";
    if (p === "/datenschutz") return "Datenschutz";
    if (p === "/kontakt") return "Kontakt";
    return p;
};

const fmtDay = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

function StatCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div data-testid={testid} className="bg-white border border-moebus-border rounded-xl p-5">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-moebus-green flex items-center justify-center">
                    <Icon size={19} />
                </div>
                <div>
                    <div className="font-heading font-bold text-2xl text-moebus-deep leading-none">{value}</div>
                    <div className="text-xs text-slate-500 mt-1">{label}</div>
                </div>
            </div>
            {sub && <div className="mt-2 text-[11px] text-slate-400">{sub}</div>}
        </div>
    );
}

function BarChart({ daily }) {
    const max = Math.max(1, ...daily.map((d) => d.visitors));
    return (
        <div data-testid="analytics-chart" className="bg-white border border-moebus-border rounded-xl p-5">
            <div className="text-sm font-semibold text-moebus-deep mb-4">Besucher pro Tag</div>
            <div className="flex items-end gap-1 h-40">
                {daily.map((d) => (
                    <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group relative">
                        <div className="absolute -top-8 hidden group-hover:block bg-moebus-deep text-white text-[10px] rounded px-2 py-1 whitespace-nowrap z-10">
                            {fmtDay(d.date)}: {d.visitors} Besucher · {d.pageviews} Aufrufe
                        </div>
                        <div
                            className="w-full max-w-[26px] rounded-t bg-moebus-green/80 hover:bg-moebus-green transition-colors"
                            style={{ height: `${Math.max(2, (d.visitors / max) * 100)}%` }}
                        ></div>
                    </div>
                ))}
            </div>
            <div className="flex gap-1 mt-1.5">
                {daily.map((d, i) => (
                    <div key={d.date} className="flex-1 text-center text-[9px] text-slate-400">
                        {(daily.length <= 14 || i % 3 === 0) ? fmtDay(d.date) : ""}
                    </div>
                ))}
            </div>
        </div>
    );
}

export const AnalyticsView = () => {
    const [days, setDays] = useState(14);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiClient.get(`/admin/analytics?days=${days}`);
            setData(r.data);
        } catch (e) {
            console.error("Analytics load failed:", e);
        }
        setLoading(false);
    }, [days]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
    }
    if (!data) return null;

    return (
        <div data-testid="analytics-view" className="max-w-5xl">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-lg font-bold text-moebus-deep">Besucher-Statistik</h2>
                    <p className="text-sm text-slate-500">Anonymisiert erhoben – ohne Cookies, ohne personenbezogene Daten.</p>
                </div>
                <div className="inline-flex items-center gap-1 bg-white border border-moebus-border rounded-lg p-1">
                    {RANGES.map(([d, label]) => (
                        <button
                            key={d}
                            data-testid={`analytics-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                days === d ? "bg-moebus-deep text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-5">
                <StatCard icon={Users} label="Besucher heute" value={data.today.visitors} testid="stat-today-visitors" />
                <StatCard icon={Eye} label="Seitenaufrufe heute" value={data.today.pageviews} testid="stat-today-pageviews" />
                <StatCard
                    icon={Users}
                    label={`Besucher gesamt (${data.days} Tage)`}
                    value={data.total.visitors}
                    sub={`${data.total.pageviews} Seitenaufrufe insgesamt`}
                    testid="stat-total-visitors"
                />
            </div>

            <div className="grid gap-5">
                <BarChart daily={data.daily} />

                <div className="grid md:grid-cols-2 gap-5">
                    <div data-testid="analytics-sources" className="bg-white border border-moebus-border rounded-xl p-5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-moebus-deep mb-3">
                            <Compass size={15} /> Herkunft der Besucher
                        </div>
                        {(!data.sources || data.sources.length === 0) && (
                            <div className="text-sm text-slate-400 py-4 text-center">Noch keine Daten.</div>
                        )}
                        <div className="grid gap-2">
                            {(data.sources || []).map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{s.source}</span>
                                    <span className="font-medium text-moebus-deep">{s.visitors}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-[11px] text-slate-400">
                            QR-Code, geteilte Links, Indeed, Suchmaschinen & Co. – gezählt pro Besucher beim ersten Aufruf.
                        </p>
                    </div>

                    <div data-testid="analytics-regions" className="bg-white border border-moebus-border rounded-xl p-5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-moebus-deep mb-3">
                            <Globe2 size={15} /> Top Regionen
                        </div>
                        {data.regions.length === 0 && (
                            <div className="text-sm text-slate-400 py-4 text-center">Noch keine Daten.</div>
                        )}
                        <div className="grid gap-2">
                            {data.regions.map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">
                                        {[r.city, r.region].filter(Boolean).join(", ") || "Unbekannt"}
                                        {r.country && <span className="text-slate-400 text-xs ml-1.5">{r.country}</span>}
                                    </span>
                                    <span className="font-medium text-moebus-deep">{r.visitors}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div data-testid="analytics-pages" className="bg-white border border-moebus-border rounded-xl p-5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-moebus-deep mb-3">
                            <FileText size={15} /> Meistbesuchte Seiten
                        </div>
                        {data.pages.length === 0 && (
                            <div className="text-sm text-slate-400 py-4 text-center">Noch keine Daten.</div>
                        )}
                        <div className="grid gap-2">
                            {data.pages.map((p, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700 truncate">{pathLabel(p.path)}</span>
                                    <span className="font-medium text-moebus-deep flex-shrink-0 ml-3">
                                        {p.views} <span className="text-xs text-slate-400 font-normal">({p.visitors} Bes.)</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs text-slate-500 bg-white border border-moebus-border rounded-lg px-4 py-3">
                    <ShieldCheck size={15} className="text-moebus-green flex-shrink-0 mt-0.5" />
                    <span>
                        DSGVO-freundlich: Es werden keine Cookies gesetzt und keine IP-Adressen gespeichert. Besucher werden über
                        einen täglich wechselnden, anonymen Kenner gezählt. Alle Daten werden nach {data.retention_days} Tagen
                        automatisch gelöscht. Suchmaschinen-Bots werden nicht mitgezählt.
                    </span>
                </div>
            </div>
        </div>
    );
};
